import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { UntypedFormGroup } from '@angular/forms';
import { Client } from 'src/providers/client/client';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Genome } from 'src/providers/genome/genome';
import { PhotoNotes } from 'src/providers/photo-notes/photo-notes';
import { Pros } from 'src/providers/pros/pros';
import { Schedule } from 'src/providers/schedule/schedule';
import { ToDos } from 'src/providers/to-dos/to-dos';
import { ToDosQueriesService } from 'src/shared/services/to-dos-queries.service';
import { ToDosApiService } from 'src/shared/services/to-dos-api.service';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { AddressModel } from 'src/models/address.model';
import { TidySelectNumberValueModel } from 'src/models/tidy-select-item.model';
import {
  RoomModel,
  RoomModelExtended,
  SectionItem,
  ChecklistSectionItem,
  TextSectionItem,
  PhotoSectionItem,
} from 'src/models/to-do.model';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { WindowService } from 'src/shared/providers/window.service';
import { Loading } from '../../shared/components/loading/loading';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Events } from 'src/providers/events/events';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { Util } from 'src/shared/util/util';
import { PhotoCaptionService } from 'src/shared/components/photo-caption/photo-caption.service';
import {
  ConfirmPage,
  ConfirmPageParamsModel,
} from 'src/pages/confirm/confirm.component';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { InputComponent } from 'src/tidy-ui-components/components/form/input/input.component';
import { lastValueFrom, Subscription } from 'rxjs';
import { toDoListMockData } from 'src/shared/constants/onboarding/to-do-list';
import { toDoAutocomplete } from 'src/shared/constants/toDoAutocomplete';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { BookService } from 'src/shared/services/book.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { BREAK_LARGE_RESOLUTION } from 'android/app/src/constants/app.contants';
import { Router } from '@angular/router';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import * as Sentry from '@sentry/angular';

@Component({
  templateUrl: 'to-dos.html',
  providers: [PhotoCaptionService],
  standalone: false,
})
export class ToDosPage implements AfterViewInit, OnDestroy {
  @ViewChild('addCardInput') addCardInput: InputComponent;
  addingTextItemRoomId: number | null = null;
  addressFilter: TidySelectNumberValueModel[];
  addressId: number;
  isViewingOrphaned = false;
  addressLoaded: boolean;
  addressResponse: AddressModel[];
  hasAddresses: boolean;
  editingPhotoItemId: number | null = null;
  editingTextItemId: number | null = null;
  errorMessage: string;
  fabButtonOpen = false;
  listLoaded: boolean;
  newTextItem: { content: string; roomId: number } | null = null;
  private tempIdCounter = -1;
  rooms: RoomModelExtended[];
  hasToDoLists: boolean;
  hasTwoOrMoreLists: boolean;
  hasJobsIsLoading: boolean;
  isBottomNavItem = false;
  isRightSideContent = true;
  jobsAppliedSentence: string;
  selectedList: any;
  selectedListName: string;
  showLoadingSpinner = false;
  taskListId: number;
  toDoFilter: TidySelectNumberValueModel[];
  toDoLists: any;
  toDosOnboarding: boolean;
  mobileDropdownMenu: { label: string; action: string }[] = [];
  contentHeight: number;
  editPrio = false;
  editingRoomId: number | null = null;
  editingRoomOriginalName: string | null = null;
  newCardName = '';
  categoryItems: any[] = [];
  customFieldTypes: any[] = [];
  editingToDoId: number | null = null;
  expandedToDos: Set<number> = new Set();
  inlineCustomFields: { [toDoId: number]: InlineCustomField[] } = {};
  taskCategoryOptions: any[] = [];
  toDoAutoComplete = toDoAutocomplete;
  inlineInventoryName: { [toDoId: number]: string } = {};
  inlineInventoryCategory: { [toDoId: number]: number } = {};
  inventorySuggestions: { [toDoId: number]: any[] } = {};
  inventorySearching: { [toDoId: number]: boolean } = {};
  loaded: boolean;
  photoUploading: { [toDoId: number]: boolean } = {};
  showInlineInventory: { [toDoId: number]: boolean } = {};
  savedToDoId: number | null = null;
  savingToDoId: number | null = null;
  autosaveTimeouts: { [key: string]: any } = {};
  backPage = 'more';
  addingToDoRoomId: number | null = null;
  newToDo: NewToDoData | null = null;
  newToDoCustomFields: InlineCustomField[] = [];
  newToDoForm: UntypedFormGroup;
  newToDoInventoryCategory: number | null = null;
  newToDoInventoryName = '';
  newToDoInventorySearching = false;
  newToDoInventorySuggestions: any[] = [];
  newToDoPhotos: any[] = [];
  newToDoPhotoUploading = false;
  newToDoSubmitted = false;
  addingPhotoRoomId: number | null = null;
  pendingSectionPhotos: any[] = [];
  sectionPhotoUploading = false;
  pendingToDoPhotos: { [toDoId: number]: any[] } = {};
  inventoryForm: UntypedFormGroup;
  inventorySubmitted = false;
  showNewToDoInventory = false;
  showAboutThisPage: boolean;
  remoteInspectionEnabled: boolean;
  remoteInspectionType = 'photos';
  remoteInspectionWhen = 'before_and_after';
  remoteInspectionTypeItems = [
    { value: 'photos', viewValue: new TranslationPipe().transform('Photos') },
    { value: 'videos', viewValue: new TranslationPipe().transform('Videos') },
  ];
  remoteInspectionWhenItems = [
    { value: 'before_and_after', viewValue: new TranslationPipe().transform('Before & After') },
    { value: 'before', viewValue: new TranslationPipe().transform('Before') },
    { value: 'after', viewValue: new TranslationPipe().transform('After') },
  ];
  private dropdownMenuSubscription$: Subscription;
  private headerIconSubscription$: Subscription;
  private resizeHandler: () => void;

  readonly ROOM_SUGGESTIONS = ['Kitchen', 'Bedroom', 'Bathroom', 'Living Room', 'Garage', 'Dining Room', 'Office', 'Laundry Room'];
  visibleSuggestions: string[] = [];

  readonly TASK_SUGGESTIONS: { [key: string]: string[] } = {
    'kitchen': ['Wash dishes', 'Clean countertops', 'Wipe down stove', 'Clean refrigerator', 'Sweep and mop floor'],
    'bedroom': ['Make bed', 'Change sheets', 'Vacuum floor', 'Dust surfaces', 'Organize closet'],
    'bathroom': ['Clean toilet', 'Clean shower/tub', 'Wipe down sink', 'Clean mirrors', 'Mop floor'],
    'living room': ['Vacuum floor', 'Dust furniture', 'Fluff pillows', 'Wipe down surfaces', 'Organize items'],
    'garage': ['Sweep floor', 'Organize tools', 'Take out trash', 'Check door opener', 'Clean windows'],
    'dining room': ['Wipe down table', 'Clean chairs', 'Vacuum/sweep floor', 'Dust surfaces', 'Polish furniture'],
    'office': ['Dust desk', 'Organize papers', 'Vacuum floor', 'Wipe down keyboard', 'Empty trash'],
    'laundry room': ['Wipe down washer/dryer', 'Clean lint trap', 'Organize supplies', 'Sweep floor', 'Check for leaks']
  };
  currentTaskSuggestions: string[] = [];

  boundGoToAddList = this.goToAddList.bind(this);

  private readonly toDosQueries = inject(ToDosQueriesService);
  private readonly toDosApi = inject(ToDosApiService);
  private aboutThisPageService = inject(AboutThisPageService);
  private bookService = inject(BookService);
  private readonly demoBlock = inject(DemoBlockService);

  constructor(
    private client: Client,
    private genome: Genome,
    private modalCtrl: ModalController,
    private navCtrl: CustomNavController,
    private photoCaptionService: PhotoCaptionService,
    private photoNotes: PhotoNotes,
    private pros: Pros,
    private schedule: Schedule,
    public toDos: ToDos,
    private currentAddress: CurrentAddress,
    public windowService: WindowService,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private events: Events,
    public onboardingProvider: OnboardingProvider,
    private onboardingDummyDataService: OnboardingDummyDataService,
    private router: Router,
    private util: Util
  ) {
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('checklists');
  }

  async ngAfterViewInit() {
    const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    this.isBottomNavItem = !isRentalClient;

    if (!this.isBottomNavItem) {
      const cameFrom = localStorage.getItem('cameFromPage');
      if (cameFrom === 'properties' || cameFrom === 'more') {
        this.backPage = cameFrom;
      }
    }
    this.isRightSideContent =
      (await this.storage.retrieve('dialog-right-side-open')) || false;

    // On desktop, require right-side panel context. On mobile, allow direct page access.
    if (this.windowService.isDesktopRes && !this.isRightSideContent) {
      this.navCtrl.navigateForward('checklists');
      return;
    }

    this.getPageData();
  }

  async getPageData() {
    if (!this.isRightSideContent) {
      this.showLoadingSpinner = true;
    }
    try {
      this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('checklists');
      this.errorMessage = '';
      this.toDosOnboarding = false;
      localStorage.removeItem('roomId');
      this.loadCategoryItems();
      await this.getAddresses();

      // Early return if no addresses and not showing onboarding
      this.hasAddresses = this.addressResponse.length > 0;
      this.loaded = true;
      if (!this.hasAddresses && !this.showAboutThisPage) {
        return;
      }

      await this.changeAddress(this.addressId, false);
      if (this.hasToDoLists) {
        this.showAlertIfRemoteInspectionWontWork();
        this.setupHeaderIcons();
      }
      this.rightSidePanelService.setDialogPageTitle(this.selectedList?.title);
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err.message;
    } finally {
      if (!this.isRightSideContent) {
        this.showLoadingSpinner = false;
      }
    }
  }

  showAlertIfRemoteInspectionWontWork() {
    const hasToDos = this.rooms?.some((room) => room?.tasks?.length > 0);
    if (
      !hasToDos &&
      this.selectedList?.before_after_photos_state !== 'inactive'
    ) {
      this.util.showError(
        'Please add at least one checklist item to this checklist to ensure that remote inspection works.',
        10000
      );
    }
  }

  dismissAboutThisPage(): void {
    localStorage.removeItem('taskListId');
    localStorage.removeItem('roomId');
    localStorage.removeItem('addressId');
    this.showAboutThisPage = false;
    this.rooms = [];
    this.toDoLists = [];
    this.selectedList = null;
    this.addressResponse = [];
    this.getPageData();
  }

  async getAddresses() {
    if (this.showAboutThisPage) {
      this.addressResponse = this.onboardingDummyDataService.getPropertiesMockData();
      this.addressFilter = this.client.parseAddressList(
        this.addressResponse,
        true
      );
      this.addressId = this.addressResponse[0].id;
    } else {
      this.addressResponse = await this.client.getAddresses();
      this.addressFilter = this.client.parseAddressList(
        this.addressResponse,
        true
      );
      this.addressId = this.client.getSelectedAddressId(this.addressResponse);
    }
    localStorage.setItem('isAllAddressesSelected', 'false');
    this.events.publish('update:addressId', this.addressId);
  }

  async getToDos() {
    if (this.isRightSideContent) {
      this.addressId = parseInt(localStorage.getItem('addressId'));
    }
    const toDosMockData = this.onboardingDummyDataService.getToDosMockData();
    if (this.showAboutThisPage) {
      this.toDoLists = toDosMockData;
      this.isViewingOrphaned = false;
    } else {
      this.toDosQueries.selectAddress(this.addressId);
      this.toDoLists = await this.toDosQueries.getListsForAddress(this.addressId);
      // A stored list that isn't tied to any active address is orphaned (its
      // property was deleted). Refetch the orphaned set so its title, metadata
      // and the checklist dropdown all resolve to the real list.
      const storedTaskListId = Number(localStorage.getItem('taskListId'));
      this.isViewingOrphaned =
        storedTaskListId > 0 &&
        !this.toDoLists.some((list) => list.id === storedTaskListId);
      if (this.isViewingOrphaned) {
        this.toDoLists = await this.toDosQueries.getOrphanedLists();
      }
    }
    this.toDoFilter = this.toDos.parseToDoFilterItems(
      this.toDoLists,
      this.addressId
    );
    this.hasToDoLists = this.toDoFilter.length !== 0;
    this.hasTwoOrMoreLists = this.toDoFilter.length > 3;
    if (this.hasToDoLists) {
      this.taskListId = this.toDos.getSelectedTaskListId(this.toDoFilter);
      await this.getToDoList();
    }
    this.listLoaded = true;
  }

  async getToDoList() {
    let rooms;
    if (this.showAboutThisPage) {
      rooms = toDoListMockData;
    } else {
      this.toDosQueries.selectList(this.taskListId);
      // Invalidate cache first to ensure fresh data when dialog opens
      this.toDosQueries.invalidateListDetail(this.taskListId);
      // Then fetch fresh data (will refetch due to invalidation)
      rooms = await this.toDosQueries.getListDetail(this.taskListId);
    }
    const roomsWithIcons = await this.toDos.buildRoomIcons(rooms);
    this.rooms = roomsWithIcons.map((room) =>
      this.transformRoomToExtended(room)
    );
    this.refreshSuggestions();
    this.selectedList = this.toDoLists.find(
      (list) => list.id === this.taskListId
    );
    if (!this.selectedList) {
      this.selectedList = this.toDoLists[0];
      this.taskListId = this.toDoLists[0].id;
    }
    this.selectedListName = this.selectedList.title;
    this.parseRemoteInspectionState(
      this.selectedList?.before_after_photos_state
    );
  }

  private transformRoomToExtended(room: RoomModel): RoomModelExtended {
    const items: SectionItem[] = [];
    let idCounter = 0;
    if (room.tasks) {
      room.tasks.forEach((task) => {
        items.push({
          id: idCounter++,
          item_type: 'checklist',
          order: task.order ?? 0,
          title: task.title,
          note: task.note,
          is_important: task.is_important,
          photo_notes: task.photo_notes || [],
          options: task.options,
          room_object: task.room_object,
          task_type: task.task_type,
          original_id: task.id,
          custom: task.custom,
          estimated_time: task.estimated_time,
          internal_priority: task.internal_priority,
          occurrence: task.occurrence,
          state: task.state,
        } as ChecklistSectionItem);
      });
    }
    if (room.notes) {
      room.notes.forEach((note) => {
        items.push({
          id: idCounter++,
          item_type: 'text',
          order: note.order ?? 0,
          content: note.body,
          original_id: note.id,
        } as TextSectionItem);
      });
    }
    if (room.photos) {
      room.photos.forEach((photo: any) => {
        items.push({
          id: idCounter++,
          item_type: 'photo',
          order: photo.order ?? 0,
          photo_url: photo.photo_url,
          photo_note_caption: photo.photo_note_caption || photo.caption || '',
          original_id: photo.id,
        } as PhotoSectionItem);
      });
    }
    // Sort all items by their order property to create a unified list
    items.sort((a, b) => a.order - b.order);
    return { ...room, items } as RoomModelExtended;
  }

  async changeAddress(addressId, removeStoredToDoList) {
    if (!this.isRightSideContent) {
      this.showLoadingSpinner = true;
    }
    try {
      this.toDosOnboarding = false;
      if (removeStoredToDoList) {
        localStorage.removeItem('taskListId');
      }
      if (addressId === 0) {
        return this.rightSidePanelService.navigateTo('add-property', {});
      }
      this.addressId = addressId;
      if (!this.showAboutThisPage) {
        this.currentAddress.addressId = addressId;
      }
      await this.getToDos();
      if (this.taskListId && !this.showAboutThisPage) {
        localStorage.setItem('taskListId', this.taskListId.toString());
      }
      this.addressLoaded = true;
      this.checkIfHasJobs();

      if (!this.hasToDoLists && !this.showAboutThisPage) {
        this.toDosOnboarding = true;
      }
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err.message;
    } finally {
      if (!this.isRightSideContent) {
        this.showLoadingSpinner = false;
      }
    }
  }

  async checkIfHasJobs() {
    if (this.isViewingOrphaned) {
      // Applied jobs hang off the deleted property, so there is nothing to show.
      this.jobsAppliedSentence = '';
      return;
    }
    this.hasJobsIsLoading = true;
    this.jobsAppliedSentence = await this.buildJobsAppliedSentence();
    this.hasJobsIsLoading = false;
  }

  async buildJobsAppliedSentence() {
    if (!this.taskListId) {
      return 'Not used for any jobs';
    }
    const jobs = await this.toDos.getAppliedJobs(this.taskListId);
    if (jobs.length === 0) {
      return 'Not used for any jobs';
    }
    const date = new ParseDateTimePipe().transform(jobs[0].date, 'MMM d');
    return `Used next on ${date}`;
  }

  async changeList(listId) {
    if (listId === -1) {
      return this.goToAddList();
    }
    if (!this.isRightSideContent) {
      this.showLoadingSpinner = true;
    }
    try {
      this.taskListId = listId;
      localStorage.setItem('taskListId', listId);
      await this.getToDoList();
      this.listLoaded = true;
    } finally {
      if (!this.isRightSideContent) {
        this.showLoadingSpinner = false;
      }
    }
  }

  goToAddToDo() {
    if (this.showAboutThisPage) {
      return;
    }
    localStorage.removeItem('roomId');
    const params = {
      rooms: this.toDos.parseRoomCheckboxItems(this.rooms),
      roomsResponse: this.rooms,
      selectedListId: this.selectedList.id,
      addressId: this.addressId,
      toDo: null,
    };
    this.rightSidePanelService.navigateTo('add-to-do', params);
  }

  goToDashboard() {
    this.navCtrl.navigateForward('properties');
  }

  startAddToDoInline(room: any) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.editingToDoId = null;
    this.addingToDoRoomId = room.id;
    this.loadTaskSuggestionsForRoom(room);
    this.newToDo = {
      title: '',
      note: '',
      is_important: false,
      roomId: room.id,
    };
    setTimeout(() => {
      const input = document.getElementById(
        `new-todo-title-input`
      ) as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }, 100);
  }

  cancelAddToDo() {
    this.addingToDoRoomId = null;
    this.newToDo = null;
    this.newToDoCustomFields = [];
    this.showNewToDoInventory = false;
    this.newToDoInventoryName = '';
    this.newToDoInventoryCategory = null;
    this.newToDoInventorySuggestions = [];
    this.newToDoPhotos = [];
    this.newToDoPhotoUploading = false;
  }

  async saveNewToDo(room: RoomModelExtended) {
    if (this.demoBlock.block()) { return; }
    if (!this.newToDo) {
      return;
    }
    const title = this.newToDo.title?.trim();
    if (!title) {
      this.util.showError('Please enter what needs to be done', 3000);
      return;
    }
    for (const field of this.newToDoCustomFields) {
      if (field.type && !field.label?.trim()) {
        this.util.showError('Please fill out the "What should pro do?" field for all custom fields', 3000);
        return;
      }
      if (field.type) {
        const validOptions = field.options.filter((opt) => opt?.trim());
        if (
          field.numberOfRequiredOptions > 0 &&
          validOptions.length < field.numberOfRequiredOptions
        ) {
          this.util.showError(
            `Please add at least ${field.numberOfRequiredOptions} option${field.numberOfRequiredOptions > 1 ? 's' : ''} for the custom field`,
            3000
          );
          return;
        }
      }
    }
    if (
      this.showNewToDoInventory &&
      this.newToDoInventoryName?.trim() &&
      !this.newToDoInventoryCategory
    ) {
      this.util.showError('Please select a category', 3000);
      return;
    }
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogLoading(true);
    }
    try {
      const taskCategoryOptions = [];
      for (const field of this.newToDoCustomFields) {
        if (!field.type || !field.label?.trim()) {
          continue;
        }
        const validOptions = field.options.filter((opt) => opt?.trim());
        const option: any = {
          id: parseInt(field.type),
          notes: field.label.trim(),
          required: field.required,
        };
        if (validOptions.length > 0) {
          option.allowed_values = validOptions.join(', ');
        }
        taskCategoryOptions.push(option);
      }
      const payload: any = {
        title,
        note: this.newToDo.note || '',
        estimated_time: 5,
        is_important: this.newToDo.is_important,
        task_category_options: taskCategoryOptions,
        address_task_group_ids: [room.id],
        address_id: this.addressId,
        address_task_list_id: this.selectedList.id,
        task_type: 'cleaning',
      };
      if (
        this.showNewToDoInventory &&
        this.newToDoInventoryName?.trim()
      ) {
        payload.room_object = {
          name: this.newToDoInventoryName.trim(),
          genome_category_id: this.newToDoInventoryCategory,
          inventory_settings: {
            strategies: [{
              type: 'task',
              inventory_type: 'count_remaining',
              address_task_lists: [{
                address_task_list_id: this.selectedList.id,
                address_task_group_ids: [room.id],
              }],
            }],
          },
        };
      }
      const tasks = await this.toDos.addToDo(
        this.addressId,
        this.taskListId,
        payload
      );
      if (tasks && tasks.length > 0) {
        const newTask = tasks[0];
        if (this.newToDoPhotos.length > 0) {
          const savePhotoFn = this.toDos.addGroupTaskPhotoNote.bind(
            this.toDos,
            room.id,
            newTask.id
          );
          const saveCaptionFn = this.photoNotes.photoCaption.bind(this.photoNotes);
          await this.photoCaptionService.uploadPhotosGeneric(
            this.newToDoPhotos,
            'task',
            savePhotoFn,
            saveCaptionFn,
            'group'
          );
          // Invalidate cache and refetch to get updated photo_notes
          this.toDosQueries.invalidateListDetail(this.taskListId);
          const updatedRooms = await this.toDosQueries.getListDetail(this.taskListId);
          const updatedRoom = updatedRooms.find((r) => r.id === room.id);
          if (updatedRoom) {
            const updatedTask = updatedRoom.tasks?.find((t) => t.id === newTask.id);
            if (updatedTask) {
              newTask.photo_notes = updatedTask.photo_notes;
            }
          }
        } else {
          // Invalidate cache for future fetches
          this.toDosQueries.invalidateListDetail(this.taskListId);
        }
        if (!room.tasks) {
          room.tasks = [];
        }
        room.tasks.push(newTask);
        const newItem: ChecklistSectionItem = {
          id: room.items ? room.items.length : 0,
          item_type: 'checklist',
          order: newTask.order ?? (room.items ? room.items.length : 0),
          title: newTask.title,
          note: newTask.note || '',
          is_important: newTask.is_important,
          photo_notes: newTask.photo_notes || [],
          options: newTask.options,
          room_object: newTask.room_object,
          task_type: newTask.task_type,
          original_id: newTask.id,
          custom: newTask.custom,
          estimated_time: newTask.estimated_time,
          internal_priority: newTask.internal_priority,
          occurrence: newTask.occurrence,
          state: newTask.state,
        };
        if (!room.items) {
          room.items = [];
        }
        room.items.push(newItem);
        // Re-sort items by order to maintain correct position
        room.items.sort((a, b) => a.order - b.order);
      }
      this.cancelAddToDo();
    } catch (err) {
      this.util.showError('Failed to add checklist item', 3000);
    } finally {
      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogLoading(false);
      }
    }
  }

  updateNewToDoField(field: string, value: any) {
    if (this.newToDo) {
      this.newToDo[field] = value;
    }
  }

  toggleNewToDoImportant() {
    if (this.newToDo) {
      this.newToDo.is_important = !this.newToDo.is_important;
    }
  }

  addNewToDoCustomField() {
    this.newToDoCustomFields.push({
      type: '',
      typeName: '',
      label: '',
      required: true,
      numberOfRequiredOptions: 0,
      options: [],
    });
  }

  removeNewToDoCustomField(index: number) {
    this.newToDoCustomFields.splice(index, 1);
  }

  onNewToDoFieldTypeChange(index: number) {
    const field = this.newToDoCustomFields[index];
    if (!field) {
      return;
    }
    const selectedOption = this.taskCategoryOptions.find(
      (opt) => opt.id === parseInt(field.type)
    );
    field.typeName = selectedOption?.name || '';
    field.numberOfRequiredOptions = 0;
    field.options = [];
    if (selectedOption?.name === 'Checkbox(es)') {
      field.numberOfRequiredOptions = 1;
      field.options = ['', ''];
    } else if (selectedOption?.name === 'Select') {
      field.numberOfRequiredOptions = 2;
      field.options = ['', ''];
    }
  }

  addNewToDoFieldOption(fieldIndex: number) {
    const field = this.newToDoCustomFields[fieldIndex];
    if (field) {
      field.options.push('');
    }
  }

  removeNewToDoFieldOption(fieldIndex: number, optionIndex: number) {
    const field = this.newToDoCustomFields[fieldIndex];
    if (field) {
      field.options.splice(optionIndex, 1);
    }
  }

  addNewToDoInventory() {
    this.showNewToDoInventory = true;
    this.newToDoInventoryName = '';
    this.newToDoInventoryCategory = null;
    this.newToDoInventorySuggestions = [];
  }

  removeNewToDoInventory() {
    this.showNewToDoInventory = false;
    this.newToDoInventoryName = '';
    this.newToDoInventoryCategory = null;
    this.newToDoInventorySuggestions = [];
  }

  async searchNewToDoInventoryItems(searchText: string) {
    this.newToDoInventoryName = searchText;
    if (!searchText || searchText.length < 1) {
      this.newToDoInventorySuggestions = [];
      return;
    }
    this.newToDoInventorySearching = true;
    try {
      const result = await this.genome.getInventoryRoomObjects({
        search: searchText,
        consumable: true,
        per_page: 10,
      });
      const items = result.body || [];
      const uniqueItems = items.reduce((acc, item) => {
        const existingItem = acc.find(
          (i) => i.name.toLowerCase() === item.name.toLowerCase()
        );
        if (!existingItem) {
          acc.push(item);
        }
        return acc;
      }, []);
      this.newToDoInventorySuggestions = uniqueItems;
    } catch (err) {
      this.newToDoInventorySuggestions = [];
    }
    this.newToDoInventorySearching = false;
  }

  selectNewToDoInventorySuggestion(item: any) {
    this.newToDoInventoryName = item.name;
    this.newToDoInventoryCategory = item.model?.category?.id || item.genome_category_id;
    this.newToDoInventorySuggestions = [];
  }

  hideNewToDoInventorySuggestions() {
    setTimeout(() => {
      this.newToDoInventorySuggestions = [];
    }, 200);
  }

  async addPhotoToNewToDo() {
    try {
      const newPhotoNotes = await this.photoCaptionService.getMultipleImages('task');
      if (!newPhotoNotes || newPhotoNotes.length === 0) {
        return;
      }
      this.newToDoPhotos = [...this.newToDoPhotos, ...newPhotoNotes];
    } catch (_err) {
      this.util.showError('Failed to add photo', 3000);
    }
  }

  removeNewToDoPhoto(index: number) {
    this.newToDoPhotos.splice(index, 1);
  }

  markNewToDoPhotoChanged(photo: any) {
    photo.captionChanged = true;
  }

  getNewToDoCustomFieldsSummary(): string {
    if (!this.newToDoCustomFields?.length) {
      return '';
    }
    const fieldDescriptions = this.newToDoCustomFields
      .map((field) => {
        if (field.label?.trim()) {
          return field.label.trim();
        }
        if (field.typeName) {
          return field.typeName;
        }
        if (field.type) {
          const typeOption = this.customFieldTypes.find((t) => t.value === parseInt(field.type));
          return typeOption?.viewValue || 'Custom field';
        }
        return 'Custom field';
      })
      .slice(0, 2);
    const summary = fieldDescriptions.join(', ');
    const remaining = this.newToDoCustomFields.length - fieldDescriptions.length;
    return remaining > 0 ? `${summary} +${remaining} more` : summary;
  }

  startAddTextItem(room: RoomModelExtended) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.editingToDoId = null;
    this.addingToDoRoomId = null;
    this.addingTextItemRoomId = room.id;
    this.newTextItem = {
      content: '',
      roomId: room.id,
    };
    setTimeout(() => {
      const input = document.getElementById(
        'new-text-item-input'
      ) as HTMLTextAreaElement;
      if (input) {
        input.focus();
      }
    }, 100);
  }

  cancelAddTextItem() {
    this.addingTextItemRoomId = null;
    this.newTextItem = null;
  }

  async saveNewTextItem(room: RoomModelExtended) {
    if (this.demoBlock.block()) { return; }
    if (!this.newTextItem || !this.newTextItem.content.trim()) {
      this.cancelAddTextItem();
      return;
    }
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogLoading(true);
    }
    try {
      const response: any = await this.toDos.addRoomNote(
        room.id,
        this.newTextItem.content.trim()
      );
      let addedNotes: any[] = [];
      if (Array.isArray(response)) {
        addedNotes = response;
      } else if (response?.text_notes) {
        addedNotes = response.text_notes;
      } else if (response?.id) {
        addedNotes = [response];
      }
      if (addedNotes.length > 0) {
        const newNote = addedNotes[0];
        const newItem: TextSectionItem = {
          id: room.items ? room.items.length : 0,
          item_type: 'text',
          order: newNote.order ?? (room.items ? room.items.length : 0),
          content: newNote.body,
          original_id: newNote.id,
        };
        if (!room.items) {
          room.items = [];
        }
        room.items.push(newItem);
        if (!room.notes) {
          room.notes = [];
        }
        room.notes.push(newNote);
        // Re-sort items by order to maintain correct position
        room.items.sort((a, b) => a.order - b.order);
        // Invalidate cache for future fetches
        this.toDosQueries.invalidateListDetail(this.taskListId);
      }
      this.cancelAddTextItem();
    } catch (err) {
      this.util.showError('Failed to add note', 3000);
    } finally {
      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogLoading(false);
      }
    }
  }

  updateNewTextItemField(value: string) {
    if (this.newTextItem) {
      this.newTextItem.content = value;
    }
  }

  enterTextEditMode(item: TextSectionItem, event: Event) {
    event.stopPropagation();
    if (this.demoBlock.block()) { return; }
    this.editingToDoId = null;
    this.editingTextItemId = item.id;
  }

  exitTextEditMode() {
    this.editingTextItemId = null;
  }

  isEditingTextItem(itemId: number): boolean {
    return this.editingTextItemId === itemId;
  }

  async saveTextItem(item: TextSectionItem, newContent: string) {
    if (this.demoBlock.block()) { return; }
    const trimmed = newContent.trim();
    if (!trimmed) {
      this.exitTextEditMode();
      return;
    }
    if (item.original_id) {
      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogLoading(true);
      }
      try {
        await this.toDos.updateRoomNote(item.original_id, trimmed);
        item.content = trimmed;
      } catch (err) {
        this.util.showError('Failed to update note', 3000);
      } finally {
        if (this.isRightSideContent) {
          this.rightSidePanelService.setDialogLoading(false);
        }
      }
    } else {
      item.content = trimmed;
    }
    this.exitTextEditMode();
  }

  enterPhotoEditMode(item: PhotoSectionItem, event: Event) {
    event.stopPropagation();
    if (this.demoBlock.block()) { return; }
    this.editingToDoId = null;
    this.editingPhotoItemId = item.id;
  }

  exitPhotoEditMode() {
    this.editingPhotoItemId = null;
  }

  isEditingPhotoItem(itemId: number): boolean {
    return this.editingPhotoItemId === itemId;
  }

  async savePhotoCaption(item: PhotoSectionItem, newCaption: string) {
    if (this.demoBlock.block()) { return; }
    const trimmed = newCaption.trim();
    if (item.original_id) {
      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogLoading(true);
      }
      try {
        await this.photoNotes.photoCaption(item.original_id, trimmed);
        item.photo_note_caption = trimmed;
      } catch (_err) {
        this.util.showError('Failed to update caption', 3000);
      } finally {
        if (this.isRightSideContent) {
          this.rightSidePanelService.setDialogLoading(false);
        }
      }
    } else {
      item.photo_note_caption = trimmed;
    }
    this.exitPhotoEditMode();
  }

  async deleteTextItem(item: TextSectionItem, room: RoomModelExtended) {
    if (this.demoBlock.block()) { return; }
    if (item.original_id) {
      try {
        await this.toDos.deleteRoomNote(item.original_id);
        if (room && room.items) {
          room.items = room.items.filter((i) => i.id !== item.id);
        }
        if (room && room.notes) {
          room.notes = room.notes.filter((n) => n.id !== item.original_id);
        }
        // Invalidate cache for future fetches
        this.toDosQueries.invalidateListDetail(this.taskListId);
      } catch (_err) {
        this.util.showError('Failed to delete note', 3000);
      }
    } else {
      if (room && room.items) {
        room.items = room.items.filter((i) => i.id !== item.id);
      }
    }
    this.exitTextEditMode();
  }

  async startAddPhotoItem(room: RoomModelExtended) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    try {
      const newPhotoNotes = await this.photoCaptionService.getMultipleImages(
        'room'
      );
      if (!newPhotoNotes || newPhotoNotes.length === 0) {
        return;
      }
      this.addingPhotoRoomId = room.id;
      this.pendingSectionPhotos = newPhotoNotes;
    } catch (err) {
      this.util.showError('Failed to add photo', 3000);
    }
  }

  cancelAddSectionPhoto() {
    this.addingPhotoRoomId = null;
    this.pendingSectionPhotos = [];
  }

  async addMoreSectionPhotos(room: RoomModelExtended) {
    try {
      const newPhotoNotes = await this.photoCaptionService.getMultipleImages('room');
      if (newPhotoNotes && newPhotoNotes.length > 0) {
        this.pendingSectionPhotos = [...this.pendingSectionPhotos, ...newPhotoNotes];
      }
    } catch (err) {
      this.util.showError('Failed to add photo', 3000);
    }
  }

  removePendingSectionPhoto(index: number) {
    this.pendingSectionPhotos.splice(index, 1);
    if (this.pendingSectionPhotos.length === 0) {
      this.cancelAddSectionPhoto();
    }
  }

  markPendingPhotoChanged(photo: any) {
    photo.captionChanged = true;
  }

  async saveSectionPhotos(room: RoomModelExtended) {
    if (this.demoBlock.block()) { return; }
    if (this.pendingSectionPhotos.length === 0) {
      this.cancelAddSectionPhoto();
      return;
    }
    this.sectionPhotoUploading = true;
    try {
      const savePhotoFn = this.toDos.addGroupPhotoNote.bind(
        this.toDos,
        room.id
      );
      const saveCaptionFn = this.photoNotes.photoCaption.bind(this.photoNotes);
      await this.photoCaptionService.uploadPhotosGeneric(
        this.pendingSectionPhotos,
        'room',
        savePhotoFn,
        saveCaptionFn,
        'group'
      );
      // Invalidate cache before refetch
      this.toDosQueries.invalidateListDetail(this.taskListId);
      await this.refreshSectionPhotos(room);
      const updatedRoom = this.rooms.find((r) => r.id === room.id);
      if (updatedRoom) {
        updatedRoom.items = this.transformRoomToExtended(
          updatedRoom as RoomModel
        ).items;
      }
      this.cancelAddSectionPhoto();
    } catch (err) {
      this.util.showError('Failed to add photo', 3000);
    } finally {
      this.sectionPhotoUploading = false;
    }
  }

  async deletePhotoItem(item: PhotoSectionItem, room: RoomModelExtended) {
    if (this.demoBlock.block()) { return; }
    if (item.original_id && item.original_id > 0) {
      try {
        await this.photoNotes.deletePhotoNotes(item.original_id);
        if (room && room.items) {
          room.items = room.items.filter((i) => i.id !== item.id);
        }
        if (room && room.photos) {
          room.photos = room.photos.filter((p) => p.id !== item.original_id);
        }
        // Invalidate cache for future fetches
        this.toDosQueries.invalidateListDetail(this.taskListId);
      } catch (err) {
        this.util.showError('Failed to delete photo', 3000);
      }
    } else {
      if (room && room.items) {
        room.items = room.items.filter((i) => i.id !== item.id);
      }
    }
  }

  async updateItemOrder(room: RoomModelExtended, orderedItems: SectionItem[]) {
    if (this.demoBlock.block()) { return; }
    orderedItems.forEach((item, index) => {
      item.order = index;
    });
    room.items = orderedItems;
    try {
      const itemsInOrder: { type: string; id: number }[] = [];
      for (const item of orderedItems) {
        const originalId = (item as any).original_id;
        if (originalId === null || originalId === undefined || isNaN(originalId)) {
          continue;
        }
        let type: string;
        switch (item.item_type) {
          case 'checklist':
            type = 'task';
            break;
          case 'text':
            type = 'note';
            break;
          case 'photo':
            type = 'photo';
            break;
          default:
            type = (item as any).item_type;
        }
        itemsInOrder.push({ type, id: originalId });
      }
      if (itemsInOrder.length === 0) {
        return;
      }
      // Use TanStack Query mutation instead of old provider method
      // The mutation's onSuccess will invalidate the cache and trigger automatic refetch
      await this.toDosQueries.setItemsOrderMutation.mutateAsync({
        groupId: room.id,
        items: itemsInOrder,
        listId: this.taskListId,
      });
      // Local state is already updated optimistically above
      // The mutation's invalidation will automatically refetch in the background
      // No need for explicit refetch - it would cause duplicate requests
    } catch (err) {
      this.util.showError('Failed to update order', 3000);
    }
  }

  async updateToDoOrder(room: any, orderedToDos: any[]) {
    try {
      const taskIds = orderedToDos
        .map((t) => t.id)
        .filter((id) => id !== null && id !== undefined && !isNaN(id));

      if (taskIds.length === 0) {
        return;
      }

      const taskIdsString = taskIds.join(',');

      const payload = {
        address_task_groups: [
          {
            address_task_group_id: room.id,
            address_task_ids_in_order: taskIdsString,
          },
        ],
      };
      await this.toDos.updateToDoOrder(this.taskListId, payload);
      room.tasks = orderedToDos;
      // Invalidate cache for future fetches
      this.toDosQueries.invalidateListDetail(this.taskListId);
    } catch (err) {
      this.util.showError('Failed to update order', 3000);
    }
  }

  async deleteRoom(room: any) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const params: ConfirmPageParamsModel = {
      title: 'Delete Section?',
      body: 'This will remove this section and all its checklist items from this checklist. You will not be able to undo this action.',
      backText: 'Go Back',
      confirmText: 'Delete Section',
      confirmAction: this.confirmDeleteRoom.bind(this, room),
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal',
    });
    await confirmationModal.present();
  }

  async confirmDeleteRoom(room: any) {
    try {
      await this.toDos.deleteGroup(room.id, this.taskListId);
      this.rooms = this.rooms.filter((r) => r.id !== room.id);
      this.refreshSuggestions();
      // Invalidate cache for future fetches
      this.toDosQueries.invalidateListDetail(this.taskListId);
      this.modalCtrl.dismiss();
    } catch (err) {
      this.util.showError('Failed to delete section', 3000);
    }
  }

  goToAddRoom() {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      list: this.selectedList,
      addressId: this.addressId,
    };
    this.rightSidePanelService.navigateTo('add-card', params);
  }

  goToAddList() {
    const params = {
      addressFilter: this.addressFilter.filter(
        (address) => address.value !== 0
      ),
      hasToDoLists: this.hasToDoLists,
    };
    this.rightSidePanelService.navigateTo('add-to-dos-list', params);
  }

  goToListSettings() {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      addressFilter: this.addressFilter,
      toDoLists: this.toDoLists,
    };
    this.rightSidePanelService.navigateTo('list-settings', params);
  }

  async editJobsApplied() {
    const allListsResponse = await this.toDos.getAllToDoLists(1000, 1, null);
    const allToDoLists = allListsResponse.body || [];
    const params = {
      listName: this.selectedListName,
      listIsDefault: this.selectedList.is_default,
      hasTwoOrMoreLists: this.hasTwoOrMoreLists,
      toDoLists: allToDoLists,
    };
    this.rightSidePanelService.navigateTo('apply-to-jobs', params);
  }

  async bookJob(bookingType) {
    if (this.demoBlock.block()) { return; }
    await this.storage.save('bookJobBackPage', 'to-dos');
    await this.client.getClientInfo();
    const address = this.addressResponse.find((ad) => ad.id === this.addressId);
    localStorage.setItem('region_id', address.region_id.toString());
    await this.bookService.goToBookJob({ address, bookingType });
  }

  listSelected(): boolean {
    return this.taskListId !== -2;
  }

  @Loading('', true)
  async updatePriorities(orderedRooms): Promise<void> {
    if (this.demoBlock.block()) { return; }
    let priorityList = '';
    orderedRooms.map((room) => {
      priorityList += `${room.id},`;
    });
    await this.toDos.updateRoomPriority(
      this.taskListId,
      priorityList.slice(0, -1)
    );
    // Invalidate cache for future fetches
    this.toDosQueries.invalidateListDetail(this.taskListId);
  }

  goToShareList() {
    const params = {
      selectedListName: this.selectedListName,
      rooms: this.rooms,
      taskListId: this.taskListId,
      selectedList: this.selectedList,
    };
    this.rightSidePanelService.navigateTo('share-list', params);
  }

  goToRemoteInspectionPage(_list: any) {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      list: this.selectedList,
      addressId: this.addressId,
      finishPage: 'to-dos',
      hasToDos: this.rooms.some((room) => room?.tasks?.length > 0),
    };
    this.rightSidePanelService.navigateTo('remote-inspection', params);
  }

  getCardIcon(room) {
    if (this.editPrio) {
      return 'assets/svg/menu-tidy-green.svg';
    } else {
      return `assets/img/icons/rooms/${room?.icon}.svg`;
    }
  }

  goToEditList() {
    const params = {
      selectedList: this.selectedList,
      addressId: this.addressId,
    };
    this.rightSidePanelService.navigateTo('rename-list', params);
  }

  @Loading('', true)
  async saveBlockCompleteOnMissingFields(value) {
    await this.toDos.saveBlockCompleteOnMissingFields(this.taskListId, value);
  }

  editPriority() {
    if (!this.showAboutThisPage) {
      this.editPrio = !this.editPrio;
    }
  }

  parseRemoteInspectionState(state: string): void {
    if (!state || state === 'inactive') {
      this.remoteInspectionEnabled = false;
      this.remoteInspectionType = 'photos';
      this.remoteInspectionWhen = 'before_and_after';
      return;
    }
    this.remoteInspectionEnabled = true;
    this.remoteInspectionType = state.includes('video') ? 'videos' : 'photos';
    if (state.includes('before_') && state.includes('_only')) {
      this.remoteInspectionWhen = 'before';
    } else if (state.includes('after_') && state.includes('_only')) {
      this.remoteInspectionWhen = 'after';
    } else {
      this.remoteInspectionWhen = 'before_and_after';
    }
  }

  buildRemoteInspectionState(): string {
    if (!this.remoteInspectionEnabled) {
      return 'inactive';
    }
    const type = this.remoteInspectionType === 'videos' ? 'video' : 'photo';
    if (this.remoteInspectionWhen === 'before') {
      return `before_${type}_only`;
    }
    if (this.remoteInspectionWhen === 'after') {
      return `after_${type}_only`;
    }
    return type;
  }

  async toggleRemoteInspection() {
    if (this.demoBlock.block()) { return; }
    const hasToDos = this.rooms?.some((room) => room?.tasks?.length > 0);
    if (!this.remoteInspectionEnabled && !hasToDos) {
      this.util.showError(
        'Please add at least one checklist item before enabling remote inspection.',
        5000
      );
      return;
    }
    this.remoteInspectionEnabled = !this.remoteInspectionEnabled;
    const state = this.buildRemoteInspectionState();
    try {
      await this.toDos.updateListBeforeAfterPhotos(this.taskListId, state);
      this.selectedList.before_after_photos_state = state;
    } catch (err) {
      this.remoteInspectionEnabled = !this.remoteInspectionEnabled;
      this.util.showError('Failed to update remote inspection setting.', 5000);
    }
  }

  async onRemoteInspectionChange() {
    if (this.demoBlock.block()) { return; }
    const state = this.buildRemoteInspectionState();
    try {
      await this.toDos.updateListBeforeAfterPhotos(this.taskListId, state);
      this.selectedList.before_after_photos_state = state;
    } catch (err) {
      this.parseRemoteInspectionState(
        this.selectedList?.before_after_photos_state
      );
      this.util.showError('Failed to update remote inspection setting.', 5000);
    }
  }

  toggleToDoExpand(toDoId: number) {
    if (this.expandedToDos.has(toDoId)) {
      this.expandedToDos.delete(toDoId);
    } else {
      this.expandedToDos.add(toDoId);
    }
  }

  isToDoExpanded(toDoId: number): boolean {
    return this.expandedToDos.has(toDoId);
  }

  goToEditToDoDirectly(toDo: any, room: any) {
    if (this.showAboutThisPage) {
      return;
    }
    const params = {
      toDo,
      roomType: room.type,
      roomId: room.id,
      rooms: this.toDos.parseRoomCheckboxItems(this.rooms),
      roomsResponse: this.rooms,
      selectedListId: this.selectedList.id,
      addressId: this.addressId,
    };
    this.rightSidePanelService.navigateTo('add-to-do', params);
  }

  hasExtraContent(toDo: any): boolean {
    return (
      (toDo?.note && toDo.note !== '') ||
      toDo?.options?.length > 0 ||
      toDo?.room_object ||
      toDo?.photo_notes?.length > 0
    );
  }

  hasComplexContent(toDo: any): boolean {
    return (
      toDo?.options?.length > 0 ||
      toDo?.room_object ||
      toDo?.photo_notes?.length > 0
    );
  }

  enterEditMode(toDo: any, event: Event, room?: any) {
    event.stopPropagation();
    if (this.showAboutThisPage || this.editPrio) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const taskId = toDo.original_id ?? toDo.id;
    this.editingToDoId = taskId;
    this.expandedToDos.add(taskId);
    this.initInlineInventory(toDo);
    this.initExistingOptionTypeIds(toDo);
    if (room) {
      this.loadTaskSuggestionsForRoom(room);
    }
  }

  exitEditMode() {
    if (this.editingToDoId !== null) {
      this.savedToDoId = this.editingToDoId;
      this.expandedToDos.delete(this.editingToDoId);
      setTimeout(() => {
        this.savedToDoId = null;
      }, 2000);
    }
    this.editingToDoId = null;
  }

  async saveAndExitEditMode(toDo: any) {
    if (this.showAboutThisPage) {
      this.exitEditMode();
      return;
    }
    if (this.demoBlock.block()) { return; }
    const taskId = this.getTaskId(toDo);
    const title = toDo.title?.trim();
    if (!title) {
      this.util.showError('Title cannot be empty', 3000);
      return;
    }

    // Validate and save any pending custom fields first
    const pendingCustomFields = this.inlineCustomFields[taskId] || [];
    for (const field of pendingCustomFields) {
      if (field.type || field.label?.trim()) {
        // Field has been started, validate it
        if (!field.type) {
          this.util.showError('Please select a field type for the custom field', 3000);
          return;
        }
        if (!field.label?.trim()) {
          this.util.showError('Please enter a label for the custom field', 3000);
          return;
        }
        const validOptions = field.options.filter((opt) => opt?.trim());
        if (field.numberOfRequiredOptions > 0 && validOptions.length < field.numberOfRequiredOptions) {
          this.util.showError(
            `Please add at least ${field.numberOfRequiredOptions} option${field.numberOfRequiredOptions > 1 ? 's' : ''} for the custom field`,
            3000
          );
          return;
        }
      }
    }

    const pendingInventoryName = this.inlineInventoryName[taskId]?.trim();
    const pendingInventoryCategory = this.inlineInventoryCategory[taskId];
    const hasPendingInventory =
      !!pendingInventoryName &&
      (pendingInventoryName !== toDo?.room_object?.name ||
        pendingInventoryCategory !== toDo?.room_object?.genome_category_id);
    if (hasPendingInventory && !pendingInventoryCategory) {
      this.util.showError('Please select a category', 3000);
      return;
    }

    this.savingToDoId = taskId;

    try {
      const newOptions = pendingCustomFields
        .filter((field) => field.type && field.label?.trim())
        .map((field) => {
          const validOptions = field.options.filter((opt) => opt?.trim());
          const newOption: any = {
            id: parseInt(field.type),
            notes: field.label.trim(),
            required: field.required,
          };
          if (validOptions.length > 0) {
            newOption.allowed_values = validOptions.join(', ');
          }
          return newOption;
        });

      const existingOptions = (toDo.options || []).map((opt) => ({
        id: opt._typeId || null,
        notes: opt.notes?.trim() || '',
        required: opt.required,
        allowed_values: (opt.allowed_values || [])
          .map((v) => (v || '').trim())
          .filter((v) => v)
          .join(', '),
      }));

      const payload: any = {
        title,
        note: toDo.note?.trim() || '',
        is_important: toDo.is_important ?? false,
        task_category_options: [...existingOptions, ...newOptions],
      };

      if (hasPendingInventory) {
        payload.room_object = {
          name: pendingInventoryName,
          genome_category_id: pendingInventoryCategory,
        };
      } else if (toDo?.room_object) {
        payload.room_object = toDo.room_object;
      }

      await this.toDos.updateToDo(taskId, payload);
      if (hasPendingInventory) {
        toDo.room_object = {
          ...(toDo.room_object || {}),
          name: pendingInventoryName,
          genome_category_id: pendingInventoryCategory,
        };
      }

      // Refresh so newly-created option IDs and server-side normalization propagate back.
      this.toDosQueries.invalidateListDetail(this.taskListId);
      const updatedRooms = await this.toDosQueries.getListDetail(this.taskListId);
      const updatedRoom = updatedRooms.find((r) =>
        r.tasks?.some((t) => t.id === taskId)
      );
      if (updatedRoom) {
        const updatedToDo = updatedRoom.tasks.find((t) => t.id === taskId);
        if (updatedToDo) {
          toDo.options = updatedToDo.options;
          this.initExistingOptionTypeIds(toDo);
        }
      }

      delete this.inlineCustomFields[taskId];
    } catch (_err) {
      this.util.showError('Failed to save changes', 3000);
    }

    this.savingToDoId = null;
    this.exitEditMode();
  }

  onWrapperClick(event: Event) {
    if (this.editingToDoId !== null) {
      const target = event.target as HTMLElement;
      if (target?.closest('.todo-item-editing, .cdk-overlay-container, .mat-mdc-autocomplete-panel, .tidy-autocomplete-panel')) {
        return;
      }
      const activeEl = document.activeElement as HTMLElement;
      if (activeEl?.closest('.todo-item-editing')) {
        return;
      }
      const editingToDo = this.findToDoById(this.editingToDoId);
      if (editingToDo) {
        this.saveAndExitEditMode(editingToDo);
      } else {
        this.exitEditMode();
      }
    }
  }

  isEditing(toDoId: number): boolean {
    return this.editingToDoId === toDoId;
  }

  private findToDoById(toDoId: number): any {
    for (const room of this.rooms) {
      const item = room.items?.find(i => this.getTaskId(i) === toDoId);
      if (item) {
        return item;
      }
    }
    return null;
  }

  private getTaskId(toDo: any): number {
    return toDo.original_id ?? toDo.id;
  }

  autoSaveToDoInline(
    toDo: any,
    field: string,
    value: string,
    debounceMs = 1000
  ) {
    const taskId = this.getTaskId(toDo);
    const key = `${taskId}-${field}`;
    if (this.autosaveTimeouts[key]) {
      clearTimeout(this.autosaveTimeouts[key]);
    }
    this.autosaveTimeouts[key] = setTimeout(() => {
      this.saveToDoInline(toDo, field, value);
      delete this.autosaveTimeouts[key];
    }, debounceMs);
  }

  async saveToDoInline(toDo: any, field: string, value: string) {
    if (this.showAboutThisPage) {
      return;
    }
    const taskId = this.getTaskId(toDo);
    const key = `${taskId}-${field}`;
    if (this.autosaveTimeouts[key]) {
      clearTimeout(this.autosaveTimeouts[key]);
      delete this.autosaveTimeouts[key];
    }
    const trimmedValue = value?.trim();
    if (field === 'title' && !trimmedValue) {
      this.util.showError('Title cannot be empty', 3000);
      return;
    }
    this.savingToDoId = taskId;
    try {
      const payload: any = {};
      payload[field] = trimmedValue;
      if (toDo?.room_object) {
        payload.room_object = toDo.room_object;
      }
      await this.toDos.updateToDo(taskId, payload);
      toDo[field] = trimmedValue;
    } catch (err) {
      this.util.showError('Failed to save changes', 3000);
    }
    this.savingToDoId = null;
  }

  async toggleImportant(toDo: any, event: Event) {
    event.stopPropagation();
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const newValue = !toDo.is_important;
    const taskId = this.getTaskId(toDo);
    toDo.is_important = newValue;
    try {
      const payload: any = { is_important: newValue };
      if (toDo?.room_object) {
        payload.room_object = toDo.room_object;
      }
      await this.toDos.updateToDo(taskId, payload);
      this.toDosQueries.invalidateListDetail(this.taskListId);
    } catch (err) {
      toDo.is_important = !newValue;
      this.util.showError('Failed to update', 3000);
    }
  }

  setupHeaderIcons() {
    if (!this.isRightSideContent) {
      this.setupMobileDropdownMenu();
      return;
    }
    this.rightSidePanelService.setHeaderIcons([
      // Applied jobs depend on the deleted property, so hide the info icon for orphaned lists.
      ...(this.isViewingOrphaned ? [] : [{
        id: 'info',
        icon: 'assets/svg/information-circle-outline.svg',
        tooltip: this.jobsAppliedSentence,
      }]),
      {
        id: 'share',
        icon: 'assets/svg/share-outline.svg',
        tooltip: 'Share List',
      },
    ]);
    const t = (key: string) => new TranslationPipe().transform(key);
    this.rightSidePanelService.setHeaderDropdownMenu([
      { label: t('Edit Name/Description'), action: 'editList' },
      { label: t('Edit Sections'), action: 'editSections' },
      { label: t('Copy Checklist'), action: 'copyList' },
      { label: t('Delete Checklist'), action: 'deleteList' },
    ]);
    this.headerIconSubscription$ =
      this.rightSidePanelService.headerIconClicked$.subscribe((iconId) => {
        this.handleHeaderIconClick(iconId);
      });
    this.dropdownMenuSubscription$ =
      this.rightSidePanelService.dropDownMenuAction$.subscribe((action) => {
        this.handleDropdownMenuAction(action);
      });
  }

  setupMobileDropdownMenu() {
    const t = (key: string) => new TranslationPipe().transform(key);
    this.mobileDropdownMenu = [
      // Applied jobs depend on the deleted property, so hide this entry for orphaned lists.
      ...(this.isViewingOrphaned ? [] : [{ label: t('View Jobs Applied'), action: 'viewJobs' }]),
      { label: t('Share List'), action: 'shareList' },
      { label: t('Edit Name/Description'), action: 'editList' },
      { label: t('Edit Sections'), action: 'editSections' },
      { label: t('List Settings'), action: 'listSettings' },
      { label: t('Copy Checklist'), action: 'copyList' },
      { label: t('Delete Checklist'), action: 'deleteList' },
    ];
    this.dropdownMenuSubscription$ =
      this.rightSidePanelService.dropDownMenuAction$.subscribe((action) => {
        this.handleDropdownMenuAction(action);
      });
  }

  handleHeaderIconClick(iconId: string) {
    switch (iconId) {
      case 'info':
        this.editJobsApplied();
        break;
      case 'share':
        this.goToShareList();
        break;
      case 'done':
        this.exitReorderMode();
        break;
    }
  }

  handleDropdownMenuAction(action: string) {
    if (!action) {
      return;
    }
    switch (action) {
      case 'viewJobs':
        this.editJobsApplied();
        break;
      case 'shareList':
        this.goToShareList();
        break;
      case 'editList':
        this.goToEditList();
        break;
      case 'editSections':
        this.enterReorderMode();
        break;
      case 'listSettings':
        this.goToListSettings();
        break;
      case 'copyList':
        this.copyList();
        break;
      case 'deleteList':
        this.deleteList();
        break;
    }
  }

  async copyList() {
    if (this.showAboutThisPage) {
      return;
    }
    const alert = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: {
        title: 'Copy Checklist?',
        body: `This will create a copy of "${this.selectedListName}".`,
        backText: 'Cancel',
        confirmText: 'Copy',
        confirmAction: () => this.confirmCopyList(),
      } as ConfirmPageParamsModel,
      animated: false,
      cssClass: 'confirm-modal',
    });
    await alert.present();
  }

  async confirmCopyList() {
    let newListId: number | null = null;
    try {
      const existingLists = await this.toDosQueries.getListsForAddress(this.addressId);
      const existingTitles = existingLists.map(l => l.title.toLowerCase());
      let copyName = `${this.selectedListName} (Copy)`;
      let counter = 2;
      while (existingTitles.includes(copyName.toLowerCase())) {
        copyName = `${this.selectedListName} (Copy ${counter})`;
        counter++;
      }

      const listPayload = {
        all_addresses: true,
        title: copyName,
        before_after_photos_state: this.selectedList?.before_after_photos_state || 'inactive',
        description: this.selectedList?.description || '',
      };
      const listResponse = await this.toDos.createList(listPayload);
      newListId = listResponse.id;

      const sourceState = this.selectedList?.before_after_photos_state || 'inactive';
      if (sourceState !== 'inactive') {
        await this.toDos.updateListBeforeAfterPhotos(newListId, sourceState);
      }

      const roomsSnapshot = this.rooms.map((room) => ({
        name: room.name,
        tasks: [...(room.tasks || [])],
        notes: [...(room.notes || [])],
        photos: [...(room.photos || [])],
      }));

      const pendingPhotoNotes: { type: string; object_id: number; photo_url: string; caption?: string }[] = [];

      for (const room of roomsSnapshot) {
        const groupResponse = await this.toDos.addGroup({ name: room.name });
        const groupId = groupResponse.id;
        await this.toDos.updateGroup(groupId, { address_task_list_id: newListId });

        for (const task of room.tasks || []) {
          const taskCategoryOptions = (task.options || []).map((opt) => {
            const typeId = this.taskCategoryOptions.find((t) => t.name === opt.name)?.id || null;
            return {
              id: typeId,
              notes: opt.notes,
              required: opt.required,
              allowed_values: Array.isArray(opt.allowed_values) ? opt.allowed_values.join(', ') : (opt.allowed_values || ''),
            };
          });
          const taskPayload: any = {
            title: task.title,
            note: task.note || '',
            estimated_time: task.estimated_time || 0,
            is_important: task.is_important || false,
            task_category_options: taskCategoryOptions,
            address_task_group_ids: [groupId],
            address_id: this.addressId,
            address_task_list_id: newListId,
            task_type: task.task_type || 'cleaning',
          };
          if (task.room_object) {
            taskPayload.room_object = {
              name: task.room_object.name,
              genome_category_id: task.room_object.genome_category_id,
              inventory_settings: {
                strategies: [{
                  type: 'task',
                  inventory_type: 'count_remaining',
                  address_task_lists: [{
                    address_task_list_id: newListId,
                    address_task_group_ids: [groupId],
                  }],
                }],
              },
            };
          }
          const newTasks = await this.toDos.addToDo(this.addressId, newListId, taskPayload);

          if (task.photo_notes?.length > 0 && newTasks?.length > 0) {
            const newTaskId = newTasks[0].id;
            for (const photo of task.photo_notes) {
              pendingPhotoNotes.push({
                type: 'task',
                object_id: newTaskId,
                photo_url: photo.photo_url,
                caption: photo.photo_note_caption || photo.caption || '',
              });
            }
          }
        }

        for (const note of room.notes || []) {
          await this.toDos.addRoomNote(groupId, note.body);
        }

        for (const photo of room.photos || []) {
          pendingPhotoNotes.push({
            type: 'group',
            object_id: groupId,
            photo_url: photo.photo_url,
            caption: photo.photo_note_caption || photo.caption || '',
          });
        }
      }

      await this.toDos.addPhotoNotesBatch(pendingPhotoNotes);

      await this.modalCtrl.dismiss();
      this.toDosQueries.invalidateLists(this.addressId);
      this.toDosQueries.invalidateListDetail(newListId);
      this.events.publish('checklist:updated');
      localStorage.setItem('taskListId', String(newListId));
      this.taskListId = newListId;
      await this.getToDos();
      if (this.taskListId !== newListId) {
        this.taskListId = newListId;
        await this.getToDoList();
      }
      this.util.showSuccess('Checklist copied successfully.');
    } catch (err) {
      if (newListId) {
        try {
          await this.toDos.deleteList(newListId);
        } catch (rollbackErr) {
          // Rollback itself failed (e.g. network down) - report it so the
          // orphaned partial list is visible instead of failing silently.
          Sentry.captureException(rollbackErr, {
            extra: { context: 'confirmCopyList rollback', newListId },
          });
        }
      }
      const errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      this.util.showError(errorMessage, 10000);
    }
  }

  async deleteList() {
    if (this.showAboutThisPage) {
      return;
    }
    const params: ConfirmPageParamsModel = {
      title: 'Delete Checklist?',
      body: 'This will permanently delete this checklist and all its sections and items. You will not be able to undo this action.',
      backText: 'Go Back',
      confirmText: 'Delete Checklist',
      confirmAction: this.confirmDeleteList.bind(this),
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal',
    });
    await confirmationModal.present();
  }

  async confirmDeleteList() {
    localStorage.removeItem('taskListId');
    try {
      await this.toDos.deleteList(this.taskListId);
      // Invalidate lists cache for the address
      this.toDosQueries.invalidateLists(this.addressId);
      await this.storage.save('shouldReloadPageAfterClosingSideRail', true);
      if (this.isRightSideContent) {
        this.navCtrl.navigateForward('checklists');
      } else {
        this.navCtrl.navigateForward('to-dos');
      }
    } catch (err) {
      this.util.showError('Failed to delete checklist', 3000);
    }
    this.modalCtrl.dismiss();
  }

  enterReorderMode() {
    if (this.showAboutThisPage) {
      return;
    }
    this.editPrio = true;
    this.rightSidePanelService.setHeaderDropdownMenu([]);
    this.rightSidePanelService.setHeaderIcons([
      {
        id: 'done',
        icon: 'assets/svg/checkmark-outline.svg',
        tooltip: 'Done',
        label: 'Done',
      },
    ]);
    /* if (focusAddInput) {
      setTimeout(() => {
        if (this.addCardInput) {
          this.addCardInput.focusNativeInput();
        }
      }, 100);
    } */
  }

  exitReorderMode() {
    this.flushPendingRoomName();
    this.editPrio = false;
    this.newCardName = '';
    this.setupHeaderIcons();
  }

  handleBackButton(): boolean {
    if (this.editPrio) {
      this.exitReorderMode();
      return false;
    }
    return true;
  }

  enterReorderModeWithAdd() {
    if (this.demoBlock.block()) { return; }
    this.enterReorderMode();
  }

  editRoomNameInline(room: any) {
    if (this.demoBlock.block()) { return; }
    // Only one input is rendered at a time, so opening another row destroys the
    // current one before its (blur) can fire. Commit it here instead of losing it.
    if (this.editingRoomId !== null && this.editingRoomId !== room.id) {
      this.flushPendingRoomName();
    }
    this.editingRoomId = room.id;
    this.editingRoomOriginalName = room.name;
    setTimeout(() => {
      const container = document.getElementById(`room-name-input-${room.id}`);
      const input = (container?.querySelector('input') as HTMLInputElement | null)
        ?? (container as HTMLInputElement | null);
      if (input) {
        input.focus?.();
        input.select?.();
      }
    }, 100);
  }

  private flushPendingRoomName(): void {
    const pendingRoom = this.rooms?.find((room) => room.id === this.editingRoomId);
    if (!pendingRoom) {
      this.editingRoomId = null;
      this.editingRoomOriginalName = null;
      return;
    }
    this.saveRoomNameInline(pendingRoom, pendingRoom.name);
  }

  async saveRoomNameInline(room: any, newName: string) {
    if (this.demoBlock.block()) { return; }
    // Enter fires this handler and the resulting focus loss immediately fires it
    // again via (blur). editingRoomId is cleared below before any await, so the
    // second call exits here instead of sending a duplicate PUT.
    if (this.editingRoomId !== room.id) {
      return;
    }
    const originalName = this.editingRoomOriginalName;
    const trimmedName = newName?.trim();
    this.editingRoomId = null;
    this.editingRoomOriginalName = null;
    // ngModel already wrote the in-progress value onto room.name, so an
    // abandoned or unchanged edit has to put the displayed name back itself.
    if (!trimmedName || trimmedName === originalName) {
      room.name = originalName;
      return;
    }
    try {
      await this.toDos.updateGroupName(room.id, { name: trimmedName });
      room.name = trimmedName;
      this.toDosQueries.invalidateListDetail(this.taskListId);
    } catch (_err) {
      room.name = originalName;
      this.util.showError('Failed to rename section', 3000);
    }
  }

  async addCardInline() {
    if (this.demoBlock.block()) { return; }
    const name = this.newCardName?.trim();
    if (!name) {
      return;
    }
    try {
      const group = await this.toDos.addGroup({ name });
      await this.toDos.updateGroup(group.id, {
        id: group.id,
        address_task_list_id: this.selectedList.id,
      });
      const newRoom = {
        id: group.id,
        name,
        icon: 'default',
        title: name,
        category: 'group',
        floor: 1,
        tasks: [],
        notes: [],
        photos: [],
        type: 'group',
        items: [],
      };
      this.rooms.push(newRoom);
      this.newCardName = '';
      // Invalidate cache for future fetches
      this.toDosQueries.invalidateListDetail(this.taskListId);
    } catch (err) {
      this.util.showError('Failed to add section', 3000);
    }
  }

  refreshSuggestions() {
    const existingNames = new Set(this.rooms?.map(r => r.name.toLowerCase()) || []);
    const available = this.ROOM_SUGGESTIONS.filter(
      r => !existingNames.has(r.toLowerCase())
    );

    // If no suggestions available, always fallback to Bedroom and Bathroom
    if (available.length === 0) {
      this.visibleSuggestions = ['Bedroom', 'Bathroom'];
      return;
    }

    // Weight Bedroom 3x and Bathroom 2x
    const weighted: string[] = [];
    for (const room of available) {
      const lowerRoom = room.toLowerCase();
      if (lowerRoom === 'bedroom') {
        weighted.push(room, room, room);
      } else if (lowerRoom === 'bathroom') {
        weighted.push(room, room);
      } else {
        weighted.push(room);
      }
    }
    const shuffled = weighted.sort(() => Math.random() - 0.5);
    // Get 2 unique suggestions
    const unique = [...new Set(shuffled)];
    this.visibleSuggestions = unique.slice(0, 2);
  }

  async addSuggestedSection(name: string) {
    if (this.demoBlock.block()) { return; }
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogLoading(true);
    }
    try {
      const group = await this.toDos.addGroup({ name });
      await this.toDos.updateGroup(group.id, {
        id: group.id,
        address_task_list_id: this.selectedList.id,
      });
      const newRoom = {
        id: group.id,
        name,
        icon: 'default',
        title: name,
        category: 'group',
        floor: 1,
        tasks: [],
        notes: [],
        photos: [],
        type: 'group',
        items: [],
      };
      this.rooms.push(newRoom);
      this.refreshSuggestions();
    } catch (err) {
      this.util.showError('Failed to add section', 3000);
    } finally {
      if (this.isRightSideContent) {
        this.rightSidePanelService.setDialogLoading(false);
      }
    }
  }

  getTaskSuggestionsForRoom(room: any): string[] {
    if (!room?.name) {
      return this.getDefaultTaskSuggestions();
    }

    // Fuzzy match room name to known room types
    const roomNameLower = room.name.toLowerCase();
    for (const roomType of Object.keys(this.TASK_SUGGESTIONS)) {
      if (roomNameLower.includes(roomType)) {
        // Return 3 random suggestions from this room type
        const suggestions = this.TASK_SUGGESTIONS[roomType];
        const shuffled = [...suggestions].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, 3);
      }
    }

    // Fallback to bedroom/bathroom weighted suggestions
    return this.getDefaultTaskSuggestions();
  }

  private getDefaultTaskSuggestions(): string[] {
    // Weight bedroom 3x and bathroom 2x
    const weightedPool = [
      ...this.TASK_SUGGESTIONS['bedroom'],
      ...this.TASK_SUGGESTIONS['bedroom'],
      ...this.TASK_SUGGESTIONS['bedroom'],
      ...this.TASK_SUGGESTIONS['bathroom'],
      ...this.TASK_SUGGESTIONS['bathroom'],
    ];
    const shuffled = [...weightedPool].sort(() => Math.random() - 0.5);
    // Get 3 unique suggestions
    const unique = [...new Set(shuffled)];
    return unique.slice(0, 3);
  }

  loadTaskSuggestionsForRoom(room: any) {
    this.currentTaskSuggestions = this.getTaskSuggestionsForRoom(room);
  }

  async applyTaskSuggestion(suggestion: string, isNewToDo: boolean, item?: any, room?: RoomModelExtended) {
    if (isNewToDo && this.newToDo && room) {
      this.newToDo.title = suggestion;
      await this.saveNewToDo(room);
    } else if (item && room) {
      item.title = suggestion;
      await this.saveToDoInline(item, 'title', suggestion);
      this.exitEditMode();
    }
  }

  async loadCategoryItems() {
    try {
      const categories = await this.genome.getCategories();
      this.categoryItems = categories
        .filter((category) => category.consumable === true)
        .map((category) => ({
          value: category.id,
          viewValue: category.name,
          icon: category.default_icon_url,
        }));
      this.taskCategoryOptions = await this.toDos.getTaskCategoryOptions();
      this.customFieldTypes = this.taskCategoryOptions
        .filter(
          (item) =>
            item.name !== 'Vacuum' &&
            item.name !== 'Mop' &&
            item.name !== 'Take Inventory'
        )
        .map((item) => ({
          value: item.id,
          viewValue: new TranslationPipe().transform(item.name),
        }));
    } catch (err) {
      console.error('Error loading categories', err);
    }
  }

  async addPhotoToToDo(toDo: any, room: any) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const taskId = this.getTaskId(toDo);
    try {
      const newPhotoNotes = await this.photoCaptionService.getMultipleImages(
        'task'
      );
      if (!newPhotoNotes || newPhotoNotes.length === 0) {
        return;
      }
      if (!this.pendingToDoPhotos[taskId]) {
        this.pendingToDoPhotos[taskId] = [];
      }
      this.pendingToDoPhotos[taskId] = [...this.pendingToDoPhotos[taskId], ...newPhotoNotes];
    } catch (err) {
      this.util.showError('Failed to add photo', 3000);
    }
  }

  removePendingToDoPhoto(toDo: any, index: number) {
    const taskId = this.getTaskId(toDo);
    if (this.pendingToDoPhotos[taskId]) {
      this.pendingToDoPhotos[taskId].splice(index, 1);
      if (this.pendingToDoPhotos[taskId].length === 0) {
        delete this.pendingToDoPhotos[taskId];
      }
    }
  }

  markPendingToDoPhotoChanged(photo: any) {
    photo.captionChanged = true;
  }

  async savePendingToDoPhotos(toDo: any, room: any) {
    if (this.demoBlock.block()) { return; }
    const taskId = this.getTaskId(toDo);
    const photos = this.pendingToDoPhotos[taskId];
    if (!photos || photos.length === 0) {
      return;
    }
    this.photoUploading[taskId] = true;
    try {
      const savePhotoFn = this.toDos.addGroupTaskPhotoNote.bind(
        this.toDos,
        room.id,
        taskId
      );
      const saveCaptionFn = this.photoNotes.photoCaption.bind(this.photoNotes);
      await this.photoCaptionService.uploadPhotosGeneric(
        photos,
        'task',
        savePhotoFn,
        saveCaptionFn,
        'group'
      );
      // Invalidate cache before refetch
      this.toDosQueries.invalidateListDetail(this.taskListId);
      await this.refreshToDoPhotos(toDo, room);
      delete this.pendingToDoPhotos[taskId];
    } catch (err) {
      this.util.showError('Failed to add photo', 3000);
    } finally {
      this.photoUploading[taskId] = false;
    }
  }

  hasPendingToDoPhotos(toDo: any): boolean {
    const taskId = this.getTaskId(toDo);
    return this.pendingToDoPhotos[taskId]?.length > 0;
  }

  async addPhotoToSection(room: any) {
    await this.startAddPhotoItem(room);
  }

  async refreshSectionPhotos(room: any) {
    const updatedRooms = await this.toDosQueries.getListDetail(this.taskListId);
    const updatedRoom = updatedRooms.find((r) => r.id === room.id);
    if (updatedRoom) {
      room.photos = updatedRoom.photos;
    }
  }

  async refreshToDoPhotos(toDo: any, room: any) {
    const updatedRooms = await this.toDosQueries.getListDetail(this.taskListId);
    const updatedRoom = updatedRooms.find((r) => r.id === room.id);
    if (updatedRoom) {
      const taskId = this.getTaskId(toDo);
      const updatedToDo = updatedRoom.tasks?.find((t) => t.id === taskId);
      if (updatedToDo) {
        const itemIndex = room.items?.findIndex((i) => this.getTaskId(i) === taskId);
        if (itemIndex !== -1) {
          room.items[itemIndex] = { ...room.items[itemIndex], photo_notes: updatedToDo.photo_notes };
        }
      }
    }
  }

  initInlineInventory(toDo: any) {
    const taskId = this.getTaskId(toDo);
    if (toDo?.room_object) {
      this.inlineInventoryName[taskId] = toDo.room_object.name || '';
      this.inlineInventoryCategory[taskId] =
        toDo.room_object.genome_category_id || null;
    }
  }

  async saveInventoryInline(toDo: any) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const taskId = this.getTaskId(toDo);
    const name = this.inlineInventoryName[taskId]?.trim();
    const categoryId = this.inlineInventoryCategory[taskId];
    if (!name) {
      return;
    }
    const currentName = toDo?.room_object?.name;
    const currentCategory = toDo?.room_object?.genome_category_id;
    if (name === currentName && categoryId === currentCategory) {
      return;
    }
    if (!categoryId) {
      this.util.showError('Please select a category', 3000);
      return;
    }
    this.savingToDoId = taskId;
    try {
      const payload = {
        room_object: {
          name,
          genome_category_id: categoryId,
        },
      };
      await this.toDos.updateToDo(taskId, payload);
      if (!toDo.room_object) {
        toDo.room_object = {};
      }
      toDo.room_object.name = name;
      toDo.room_object.genome_category_id = categoryId;
    } catch (err) {
      this.util.showError('Failed to save inventory', 3000);
    }
    this.savingToDoId = null;
  }

  async removeInventory(toDo: any) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const taskId = this.getTaskId(toDo);
    if (toDo?.room_object) {
      this.savingToDoId = taskId;
      try {
        const payload = { room_object: null };
        await this.toDos.updateToDo(taskId, payload);
        toDo.room_object = null;
      } catch (err) {
        this.util.showError('Failed to remove inventory', 3000);
      }
      this.savingToDoId = null;
    }
    delete this.inlineInventoryName[taskId];
    delete this.inlineInventoryCategory[taskId];
    delete this.showInlineInventory[taskId];
  }

  addInlineInventory(toDo: any) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const taskId = this.getTaskId(toDo);
    this.showInlineInventory[taskId] = true;
    this.inlineInventoryName[taskId] = '';
    this.inlineInventoryCategory[taskId] = null;
    this.inventorySuggestions[taskId] = [];
  }

  async searchInventoryItems(toDo: any, searchText: string) {
    const taskId = this.getTaskId(toDo);
    this.inlineInventoryName[taskId] = searchText;
    if (!searchText || searchText.length < 1) {
      this.inventorySuggestions[taskId] = [];
      return;
    }
    this.inventorySearching[taskId] = true;
    try {
      const result = await this.genome.getInventoryRoomObjects({
        search: searchText,
        consumable: true,
        per_page: 10,
      });
      const items = result.body || [];
      const uniqueItems = items.reduce((acc, item) => {
        const existingItem = acc.find(
          (i) => i.name.toLowerCase() === item.name.toLowerCase()
        );
        if (!existingItem) {
          acc.push(item);
        }
        return acc;
      }, []);
      this.inventorySuggestions[taskId] = uniqueItems;
    } catch (err) {
      this.inventorySuggestions[taskId] = [];
    }
    this.inventorySearching[taskId] = false;
  }

  selectInventorySuggestion(toDo: any, item: any) {
    const taskId = this.getTaskId(toDo);
    this.inlineInventoryName[taskId] = item.name;
    this.inlineInventoryCategory[taskId] = item.model?.category?.id || item.genome_category_id;
    this.inventorySuggestions[taskId] = [];
    this.saveInventoryInline(toDo);
  }

  hideInventorySuggestions(toDo: any) {
    const taskId = this.getTaskId(toDo);
    setTimeout(() => {
      this.inventorySuggestions[taskId] = [];
    }, 200);
  }

  addInlineCustomField(toDo: any) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const taskId = this.getTaskId(toDo);
    if (!this.inlineCustomFields[taskId]) {
      this.inlineCustomFields[taskId] = [];
    }
    this.inlineCustomFields[taskId].push({
      type: '',
      typeName: '',
      label: '',
      required: true,
      numberOfRequiredOptions: 0,
      options: [],
    });
  }

  removeInlineCustomField(toDo: any, index: number) {
    const taskId = this.getTaskId(toDo);
    if (this.inlineCustomFields[taskId]) {
      this.inlineCustomFields[taskId].splice(index, 1);
    }
  }

  onInlineFieldTypeChange(toDo: any, index: number) {
    const taskId = this.getTaskId(toDo);
    const field = this.inlineCustomFields[taskId]?.[index];
    if (!field) {
      return;
    }
    const selectedOption = this.taskCategoryOptions.find(
      (opt) => opt.id === parseInt(field.type)
    );
    field.typeName = selectedOption?.name || '';
    field.numberOfRequiredOptions = 0;
    field.options = [];
    if (selectedOption?.name === 'Checkbox(es)') {
      field.numberOfRequiredOptions = 1;
      field.options = ['', ''];
    } else if (selectedOption?.name === 'Select') {
      field.numberOfRequiredOptions = 2;
      field.options = ['', ''];
    }
  }

  addInlineFieldOption(toDo: any, fieldIndex: number) {
    const taskId = this.getTaskId(toDo);
    const field = this.inlineCustomFields[taskId]?.[fieldIndex];
    if (field) {
      field.options.push('');
    }
  }

  removeInlineFieldOption(toDo: any, fieldIndex: number, optionIndex: number) {
    const taskId = this.getTaskId(toDo);
    const field = this.inlineCustomFields[taskId]?.[fieldIndex];
    if (field) {
      field.options.splice(optionIndex, 1);
    }
  }

  toggleInlineFieldRequired(toDo: any, index: number) {
    const taskId = this.getTaskId(toDo);
    const field = this.inlineCustomFields[taskId]?.[index];
    if (field) {
      field.required = !field.required;
    }
  }

  async saveInlineCustomField(toDo: any, index: number) {
    if (this.showAboutThisPage) {
      return;
    }
    const taskId = this.getTaskId(toDo);
    const field = this.inlineCustomFields[taskId]?.[index];
    if (!field) {
      return;
    }
    if (!field.type) {
      this.util.showError('Please select a field type', 3000);
      return;
    }
    if (!field.label?.trim()) {
      this.util.showError('Please enter a field label', 3000);
      return;
    }
    const validOptions = field.options.filter((opt) => opt?.trim());
    if (
      field.numberOfRequiredOptions > 0 &&
      validOptions.length < field.numberOfRequiredOptions
    ) {
      this.util.showError(
        `Please add at least ${field.numberOfRequiredOptions} option${
          field.numberOfRequiredOptions > 1 ? 's' : ''
        }`,
        3000
      );
      return;
    }
    this.savingToDoId = taskId;
    try {
      const existingOptions = (toDo.options || []).map((opt) => ({
        id: opt._typeId || null,
        notes: opt.notes,
        required: opt.required,
        allowed_values: opt.allowed_values?.join(', ') || '',
      }));
      const newOption: any = {
        id: parseInt(field.type),
        notes: field.label.trim(),
        required: field.required,
      };
      if (validOptions.length > 0) {
        newOption.allowed_values = validOptions.join(', ');
      }
      const payload: any = {
        task_category_options: [...existingOptions, newOption],
      };
      if (toDo?.room_object) {
        payload.room_object = toDo.room_object;
      }
      await this.toDos.updateToDo(taskId, payload);
      // Invalidate cache before refetch
      this.toDosQueries.invalidateListDetail(this.taskListId);
      const updatedRooms = await this.toDosQueries.getListDetail(this.taskListId);
      const updatedRoom = updatedRooms.find((r) =>
        r.tasks?.some((t) => t.id === taskId)
      );
      if (updatedRoom) {
        const updatedToDo = updatedRoom.tasks.find((t) => t.id === taskId);
        if (updatedToDo) {
          toDo.options = updatedToDo.options;
        }
      }
      const savedOption = toDo.options?.find(
        (opt) => opt.notes === field.label.trim()
      );
      if (savedOption) {
        field.savedOptionId = savedOption.id;
        this.inlineCustomFields[taskId].splice(index, 1);
      }
    } catch (err) {
      this.util.showError('Failed to add field', 3000);
    }
    this.savingToDoId = null;
  }

  async saveCustomFieldLabel(toDo: any, option: any, newLabel: string) {
    if (this.showAboutThisPage) {
      return;
    }
    const trimmedLabel = newLabel?.trim();
    if (!trimmedLabel) {
      return;
    }
    const taskId = this.getTaskId(toDo);
    this.savingToDoId = taskId;
    try {
      const updatedOptions = toDo.options.map((opt) => {
        if (opt === option) {
          return { ...opt, notes: trimmedLabel };
        }
        return opt;
      });
      const payload: any = {
        task_category_options: updatedOptions.map((opt) => ({
          id: opt._typeId || null,
          notes: opt.notes,
          required: opt.required,
          allowed_values: opt.allowed_values?.join(', ') || '',
        })),
      };
      if (toDo?.room_object) {
        payload.room_object = toDo.room_object;
      }
      await this.toDos.updateToDo(taskId, payload);
      option.notes = trimmedLabel;
    } catch (err) {
      this.util.showError('Failed to save field', 3000);
    }
    this.savingToDoId = null;
  }

  getFieldTypeName(option: any): string {
    return option?.name || 'Custom Field';
  }

  getExistingFieldRequiredOptions(option: any): number {
    if (option?.name === 'Select') {
      return 2;
    }
    if (option?.name === 'Checkbox(es)') {
      return 1;
    }
    return 0;
  }

  removeExistingOption(toDo: any, optionIndex: number) {
    if (toDo.options) {
      toDo.options.splice(optionIndex, 1);
    }
  }

  addExistingOptionValue(toDo: any, optionIndex: number) {
    const option = toDo.options?.[optionIndex];
    if (option) {
      if (!option.allowed_values) {
        option.allowed_values = [];
      }
      option.allowed_values.push('');
    }
  }

  removeExistingOptionValue(toDo: any, optionIndex: number, valueIndex: number) {
    const option = toDo.options?.[optionIndex];
    if (option?.allowed_values) {
      option.allowed_values.splice(valueIndex, 1);
    }
  }

  initExistingOptionTypeIds(toDo: any) {
    if (!toDo.options) {
      return;
    }
    for (const option of toDo.options) {
      const tco = this.taskCategoryOptions.find((t) => t.name === option.name);
      option._typeId = tco?.id || null;
    }
  }

  onExistingFieldTypeChange(toDo: any, optionIndex: number) {
    const option = toDo.options?.[optionIndex];
    if (!option) {
      return;
    }
    const selectedOption = this.taskCategoryOptions.find(
      (opt) => opt.id === option._typeId
    );
    option.name = selectedOption?.name || '';
    option.allowed_values = [];
    if (selectedOption?.name === 'Checkbox(es)' || selectedOption?.name === 'Select') {
      option.allowed_values = ['', ''];
    }
  }

  isOptionInInlineFields(toDo: any, optionId: number): boolean {
    const taskId = this.getTaskId(toDo);
    const fields = this.inlineCustomFields[taskId];
    if (!fields) {
      return false;
    }
    return fields.some((f) => f.savedOptionId === optionId);
  }

  async deleteToDo(toDo: any) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    const params: ConfirmPageParamsModel = {
      title: 'Delete Checklist Item?',
      body: 'You will not be able to undo this action.',
      backText: 'Go Back',
      confirmText: 'Delete Checklist Item',
      confirmAction: this.confirmDeleteToDo.bind(this, toDo),
    };
    const confirmationModal = await this.modalCtrl.create({
      component: ConfirmPage,
      componentProps: params,
      animated: false,
      cssClass: 'confirm-modal',
    });
    await confirmationModal.present();
  }

  async confirmDeleteToDo(toDo: any) {
    try {
      const taskId = this.getTaskId(toDo);
      await this.toDos.deleteToDo(taskId);
      for (const room of this.rooms) {
        if (room.tasks) {
          room.tasks = room.tasks.filter((t) => t.id !== taskId);
        }
        if (room.items) {
          room.items = room.items.filter((item) => {
            if (item.item_type === 'checklist') {
              return (item as ChecklistSectionItem).original_id !== taskId;
            }
            return true;
          });
        }
      }
      // Invalidate cache for future fetches
      this.toDosQueries.invalidateListDetail(this.taskListId);
      this.exitEditMode();
      this.modalCtrl.dismiss();
    } catch (err) {
      this.util.showError('Failed to delete checklist item', 3000);
    }
  }

  truncateText(text: string, maxLength = 50): string {
    if (!text) {
      return '';
    }
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  }

  getCustomFieldsSummary(toDo: any): string {
    if (!toDo?.options?.length) {
      return '';
    }
    const summaries = toDo.options
      .map((opt) => {
        const fieldName = opt.notes || 'Custom field';
        const typeOption = this.taskCategoryOptions.find((tco) => tco.id === opt.id);
        const typeName = typeOption?.name || opt.name || '';
        let optionsText = '';
        if (opt.allowed_values) {
          const values = Array.isArray(opt.allowed_values)
            ? opt.allowed_values
            : opt.allowed_values.split(',').map((v: string) => v.trim());
          if (values.length > 0) {
            optionsText = ` (${values.join(', ')})`;
          }
        }
        return `${fieldName}${typeName ? ` - ${typeName}` : ''}${optionsText}`;
      })
      .slice(0, 2);
    const summary = summaries.join(', ');
    const remaining = toDo.options.length - summaries.length;
    return remaining > 0 ? `${summary} +${remaining} more` : summary;
  }

  getPhotoCount(toDo: any): number {
    return toDo?.photo_notes?.length || 0;
  }

  hasNoSections(): boolean {
    return !this.rooms || this.rooms.length === 0;
  }

  ngOnDestroy() {
    if (this.headerIconSubscription$) {
      this.headerIconSubscription$.unsubscribe();
    }
    if (this.dropdownMenuSubscription$) {
      this.dropdownMenuSubscription$.unsubscribe();
    }
    this.rightSidePanelService.clearHeaderIcons();
    this.rightSidePanelService.clearHeaderDropdownMenu();
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  ionViewWillLeave() {
    // Reserved for future cleanup if needed
  }
}

interface InlineCustomField {
  type: string;
  typeName?: string;
  label: string;
  required: boolean;
  numberOfRequiredOptions: number;
  options: string[];
  savedOptionId?: number;
}

interface NewToDoData {
  title: string;
  note: string;
  is_important: boolean;
  roomId: number;
  [key: string]: any;
}
